import {
  compact,
  type Fn,
  getOverlap,
  hasOverlap,
  type Optional,
  type OptionFn,
} from '@quromedical/utils';
import type { DeviceUseStatement, Reference } from 'fhir/r4';

import type { DeviceUseStatementCode } from '../coding';
import { mergeCodes, type RequiredCodeableConcept } from './coding';
import { getReferenceId } from './id';
import type { CreateReference } from './reference';

/**
 * Check if an deviceUseStatement contains has an active state or not
 * @param deviceUseStatement
 */
export const isDeviceUseStatementActive = (
  deviceUseStatement?: DeviceUseStatement,
): boolean => deviceUseStatement?.status === 'active';

export interface LinkedSubjectAndDevice {
  subjectId?: Reference;
  deviceId?: Reference;
}

export const mapDeviceUseStatementToSubjectAndDevice = (
  deviceUseStatement: DeviceUseStatement,
): LinkedSubjectAndDevice => ({
  subjectId: deviceUseStatement?.subject,
  deviceId: deviceUseStatement?.device,
});

export const getDeviceIdFromDeviceUseStatements = (
  deviceUseStatements: DeviceUseStatement[],
): string[] => {
  const deviceIds = deviceUseStatements.map((o) => {
    const link = mapDeviceUseStatementToSubjectAndDevice(o);
    const id = getReferenceId(link.deviceId);
    return id;
  });

  return compact(deviceIds);
};

/**
 * Set the deviceUseStatement's effectivePeriod's end to now
 */
export const setDeviceUseStatementEnd = (
  o: DeviceUseStatement,
): DeviceUseStatement => {
  const end = new Date().toISOString();

  const timingPeriod = o.timingPeriod
    ? {
        ...o.timingPeriod,
        end,
      }
    : { end };

  return {
    ...o,
    status: 'completed',
    timingPeriod,
  };
};

export type DeviceUseStatementStatus = DeviceUseStatement['status'];

export type RequiredCodeableDeviceUseStatement =
  RequiredCodeableConcept<DeviceUseStatementCode>;
export interface CreateDeviceUseStatement
  extends Required<Pick<DeviceUseStatement, 'resourceType'>> {
  subject: CreateReference;
  device: CreateReference;
  reasonCode: RequiredCodeableDeviceUseStatement[];
}

export const isDeviceUseStatementOfType = (
  deviceUseStatement: DeviceUseStatement,
  deviceType: DeviceUseStatementCode,
): boolean => mergeCodes(deviceUseStatement.reasonCode).includes(deviceType);

export const isActiveDeviceUseStatement = (
  deviceUseStatement: DeviceUseStatement,
  deviceType: DeviceUseStatementCode,
): boolean => {
  const isExpectedDeviceType = isDeviceUseStatementOfType(
    deviceUseStatement,
    deviceType,
  );
  if (!isExpectedDeviceType) {
    return false;
  }

  const isActive = deviceUseStatement.status === 'active';
  const isPeriodIncomplete = isDeviceUseStatementActive(deviceUseStatement);

  return isActive || isPeriodIncomplete;
};

type TimeFrameFilter = (value: DeviceUseStatement) => boolean;

/**
 * @deprecated do not use this method of time frame filtering since it only returns the valid device
 * use statements and does not contain the details for the timings of the statements and the overlap
 * details
 */
export const createDeviceUseStatementTimeframeFilter =
  (endTime: Date, startTime: Date): TimeFrameFilter =>
  (o) => {
    if (!o?.timingPeriod?.start) {
      return false;
    }

    const { start, end } = o.timingPeriod;
    const obsStartTime = new Date(start);
    const startsBeforeEnd = obsStartTime < endTime;

    if (!end) {
      return startsBeforeEnd;
    }

    const obsEndTime = new Date(end);

    const overlaps = hasOverlap(startTime, endTime, obsStartTime, obsEndTime);

    return overlaps;
  };

interface DeviceTiming {
  deviceName: string;
  start: number;
  end?: number;
}

const getTime = (fhirDateTime?: string): number | undefined => {
  if (!fhirDateTime) {
    return undefined;
  }

  return new Date(fhirDateTime).getTime();
};

export type DeviceUseStatementForTiming = Pick<
  DeviceUseStatement,
  'device' | 'timingPeriod'
>;

/**
 * Get the Timing for the DeviceUseStatement normalized normalized in the format needed for checking
 * for overlaps. Note that the device name is used as per the reference display
 */
const getDeviceUseTiming = (
  deviceUseStatement: DeviceUseStatementForTiming,
): DeviceTiming | undefined => {
  const { device, timingPeriod } = deviceUseStatement;

  const deviceName = device.display;
  if (!deviceName) {
    return undefined;
  }

  const start = getTime(timingPeriod?.start);
  if (!start) {
    return undefined;
  }

  const end = getTime(timingPeriod?.end);
  return {
    deviceName,
    start,
    end,
  };
};

interface GetDeviceUseStatementOverlapPeriodParams {
  deviceUseStatement: DeviceUseStatementForTiming;
  /**
   * The start of the period of interest as a timestamp
   */
  from: number;
  /**
   * The end of the period of interest as a timestamp
   */
  to: number;
  /**
   * The timestamp to use for the end of the device use statement if it does not have an end
   *
   * @default value of the `to` parameter
   */
  timingEndFallback?: number;
}

/**
 * A fully resolved device timing which has a defined start and end
 */
export type DeviceTimingOverlap = Required<DeviceTiming>;

export const getDeviceUseStatementOverlapPeriod: OptionFn<
  GetDeviceUseStatementOverlapPeriodParams,
  DeviceTimingOverlap
> = ({ deviceUseStatement, from, to, timingEndFallback = to }) => {
  const timing = getDeviceUseTiming(deviceUseStatement);
  if (!timing) {
    return undefined;
  }

  const overlap = getOverlap(
    from,
    to,
    timing.start,
    timing.end || timingEndFallback,
  );
  if (!overlap) {
    return undefined;
  }

  const { deviceName } = timing;
  const { start, end } = overlap;

  return {
    deviceName,
    start,
    end,
  };
};

interface GetDeviceUseStatementOverlapPeriodsParams
  extends Omit<GetDeviceUseStatementOverlapPeriodParams, 'deviceUseStatement'> {
  deviceUseStatements: DeviceUseStatementForTiming[];
}

export const getDeviceUseStatementOverlapPeriods: Fn<
  GetDeviceUseStatementOverlapPeriodsParams,
  Optional<DeviceTimingOverlap>[]
> = ({ deviceUseStatements, ...rest }) =>
  deviceUseStatements.map((deviceUseStatement) =>
    getDeviceUseStatementOverlapPeriod({
      ...rest,
      deviceUseStatement,
    }),
  );
